import { evaluateExpression, FunctionRegistry, isTruthy } from '../expressions/expressions.js';
import { canonicallyEqual } from '../model/canonical-json.js';
import { aggregateScope, aggregateTextScope, formAggregates } from './form-layout.js';
/** Bornes de sûreté (ADR-144 §5). */
export const REACTION_MAX_DEPTH = 4;
export const REACTION_MAX_ACTIONS = 50;
const functions = FunctionRegistry.standard();
function attempt(expression, scope) {
    try {
        return evaluateExpression(expression, scope, { functions });
    }
    catch {
        return undefined;
    }
}
function holds(expression, scope) {
    return expression === undefined || expression === null ? true : isTruthy(attempt(expression, scope));
}
function empty(value) {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}
const same = (a, b) => canonicallyEqual(a ?? null, b ?? null);
function clone(value) {
    return value === undefined ? value : JSON.parse(JSON.stringify(value));
}
const isList = (section) => section.series !== undefined || section.repeat !== undefined;
/** Réactions déclarées du gabarit (1.10.0) ; aucune pour un gabarit d'avant. */
export function reactionsOf(template) {
    return (template.reactions ?? []);
}
function locate(template, ref) {
    if (ref === undefined)
        return undefined;
    const section = template.sections.find((candidate) => candidate.section_id === ref.section_id);
    const entry = section?.fields.find((candidate) => candidate.field.field_id === ref.field_id);
    return section === undefined || entry === undefined ? undefined : { section, entry, list: isList(section) };
}
/** Partie à lignes du déclencheur (changement d'un champ de liste, ajout de ligne), sinon `null` (la fiche). */
function triggerList(template, reaction) {
    if (reaction.trigger.on === 'row_add')
        return reaction.trigger.section_id ?? null;
    if (reaction.trigger.on === 'change') {
        const source = locate(template, reaction.trigger.field);
        return source !== undefined && source.list ? source.section.section_id : null;
    }
    return null;
}
const plainPath = (fieldId) => `answers.${fieldId}`;
const rowPath = (sectionId, index, fieldId) => `answers.${sectionId}.${index}.${fieldId}`;
function rowsOf(answers, sectionId) {
    const rows = answers[sectionId];
    return Array.isArray(rows) ? rows : [];
}
function plainScope(answers, inputs) {
    return { answers, form: answers, inputs };
}
function rowScope(answers, sectionId, index, inputs) {
    return { answers: rowsOf(answers, sectionId)[index] ?? {}, form: answers, inputs };
}
/** Valeur d'un chemin de réponse. */
export function answerAt(answers, path) {
    const parts = path.split('.').slice(1);
    let value = answers;
    for (const part of parts) {
        if (value === null || value === undefined)
            return undefined;
        value = Array.isArray(value) && /^\d+$/.test(part) ? value[Number(part)] : typeof value === 'object' ? value[part] : undefined;
    }
    return value;
}
function writeAt(answers, path, value) {
    const parts = path.split('.').slice(1);
    if (parts.length === 1) {
        if (value === undefined)
            delete answers[parts[0]];
        else
            answers[parts[0]] = value;
        return;
    }
    const [sectionId, index, fieldId] = parts;
    const rows = rowsOf(answers, sectionId);
    const row = rows[Number(index)];
    if (row === undefined)
        return;
    if (value === undefined)
        delete row[fieldId];
    else
        row[fieldId] = value;
}
/** Valeurs d'énumération proposées par une expression `options`, dans l'ordre de `enum_values`. */
function allowedOptions(entry, result) {
    if (!Array.isArray(result))
        return undefined;
    const values = (entry.field.validation?.enum_values ?? []);
    const wanted = new Set(result.filter((value) => typeof value === 'string'));
    return values.filter((value) => wanted.has(value));
}
/** Filtre d'un `reload` évalué : chaque valeur d'opérateur est une expression (un littéral reste lui-même). */
function evaluatedFilter(filter, scope) {
    const out = {};
    if (filter === null || typeof filter !== 'object')
        return out;
    for (const [field, raw] of Object.entries(filter)) {
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
            continue;
        const ops = {};
        for (const [op, expression] of Object.entries(raw)) {
            const value = attempt(expression, scope);
            ops[op] = value === undefined ? null : value;
        }
        out[field] = ops;
    }
    return out;
}
/** Actions dérivées applicables (`state`, `reload` sur `change` ou `open`), dans l'ordre déclaré. */
function derivedActions(template) {
    const out = [];
    for (const reaction of reactionsOf(template)) {
        if (reaction.trigger.on !== 'change' && reaction.trigger.on !== 'open')
            continue;
        const group = triggerList(template, reaction);
        for (const action of reaction.actions ?? []) {
            if (action['action'] !== 'state' && action['action'] !== 'reload')
                continue;
            const target = locate(template, action['target']);
            if (action['target'] !== undefined && target === undefined)
                continue;
            if (target !== undefined) {
                // Une liste ne règle que ses propres lignes ; la fiche règle la fiche et toutes les lignes (gate : REACTION_STATE_OUTSIDE_ROW).
                if (group !== null && (!target.list || target.section.section_id !== group))
                    continue;
            }
            else if (group !== null)
                continue;
            out.push({ reaction, action, ...(target === undefined ? {} : { target }), group });
        }
    }
    return out;
}
/** Chemins visés par une action dérivée et la portée où sa condition s'évalue. */
function targetsOf(derived, answers, inputs) {
    const { target, group } = derived;
    if (target === undefined)
        return [];
    const fieldId = target.entry.field.field_id;
    if (!target.list)
        return [{ path: plainPath(fieldId), scope: plainScope(answers, inputs), entry: target.entry }];
    const sectionId = target.section.section_id;
    const rows = rowsOf(answers, sectionId);
    return rows.map((_row, index) => ({ path: rowPath(sectionId, index, fieldId), scope: group === null ? plainScope(answers, inputs) : rowScope(answers, sectionId, index, inputs), entry: target.entry }));
}
/** Effet dérivé d'une action `state` : `true` quand la condition tient. */
function applyState(state, effect, active) {
    if (effect === 'show')
        state.visible = (state.visible ?? true) && active;
    else if (effect === 'hide')
        state.visible = (state.visible ?? true) && !active;
    else if (effect === 'require')
        state.required = (state.required ?? false) || active;
    else if (effect === 'readonly')
        state.readonly = (state.readonly ?? false) || active;
}
/**
 * États dérivés des réactions sur des réponses (ADR-144 §3.3) : visibilité, obligation, lecture seule,
 * valeurs proposées, filtre du sélecteur, visibilité des pages, champs à vider. Ne dépend que des valeurs.
 */
export function reactionStates(template, answers, inputs = {}) {
    const fields = {};
    const pages = {};
    const clears = [];
    const stateOf = (path) => (fields[path] ??= {});
    for (const derived of derivedActions(template)) {
        const { action } = derived;
        const kind = action['action'];
        if (kind === 'state' && derived.target === undefined) {
            const page = action['target_page'];
            if (page === undefined)
                continue;
            const active = holds(action['when'], plainScope(answers, inputs));
            const effect = action['effect'];
            if (effect === 'show' || effect === 'hide')
                pages[page] = (pages[page] ?? true) && (effect === 'show' ? active : !active);
            continue;
        }
        for (const { path, scope, entry } of targetsOf(derived, answers, inputs)) {
            const active = holds(action['when'], scope);
            if (kind === 'state') {
                const effect = String(action['effect']);
                applyState(stateOf(path), effect, active);
                const hidden = (effect === 'show' && !active) || (effect === 'hide' && active);
                if (hidden && action['otherwise'] === 'reverse_and_clear' && !clears.includes(path))
                    clears.push(path);
                continue;
            }
            if (!active)
                continue;
            const state = stateOf(path);
            if (action['options'] !== undefined) {
                const allowed = allowedOptions(entry, attempt(action['options'], scope));
                if (allowed !== undefined)
                    state.options = state.options === undefined ? allowed : state.options.filter((value) => allowed.includes(value));
            }
            if (action['filter'] !== undefined)
                state.filter = { ...(state.filter ?? {}), ...evaluatedFilter(action['filter'], scope) };
        }
    }
    return { fields, pages, clears };
}
/**
 * Champs d'une ligne de série tels que l'écran les rend (ADR-146 addendum (21)) : les choix rechargés et le
 * filtre ajouté au sélecteur **par sa propre ligne** (`reload` d'une réaction partie de la ligne), que le
 * gabarit effectif ne peut pas porter (il vaut pour toutes les lignes). `entries` sont les champs de la partie
 * dans le gabarit rendu (effectif) ; `states` vient de {@link reactionStates} sur les mêmes réponses. Un champ
 * sans état de ligne est rendu tel quel (même objet).
 */
export function rowEntries(entries, states, sectionId, index) {
    return entries.map((entry) => {
        const state = states.fields[rowPath(sectionId, index, entry.field.field_id)];
        if (state === undefined || (state.options === undefined && state.filter === undefined))
            return entry;
        const copy = clone(entry);
        const field = copy['field'];
        const validation = field['validation'];
        if (state.options !== undefined && validation !== undefined) {
            const current = validation['enum_values'] ?? [];
            validation['enum_values'] = current.filter((value) => state.options.includes(value));
        }
        if (state.filter !== undefined) {
            const presentation = (copy['presentation'] ??= {});
            const selector = (presentation['selector'] ??= {});
            const filter = (selector['filter'] ??= {});
            for (const [key, value] of Object.entries(state.filter))
                if (filter[key] === undefined)
                    filter[key] = value;
        }
        return copy;
    });
}
/**
 * Références à relire à la complétion (ADR-144 §8, `FORM_REFERENCE_FILTER_MISMATCH`) : chaque champ
 * `reference` visible dont une réaction `reload` filtre le sélecteur dans l'état final de la fiche, avec la
 * valeur choisie. Le serveur relit l'objet sous l'autorisation de l'identité, avec ce filtre : hors filtre, la
 * fiche est refusée. Calcul pur ; la lecture est un port du Work Engine.
 */
export function referenceFilterChecks(template, answers, inputs = {}) {
    const checks = [];
    const states = reactionStates(template, answers, inputs);
    for (const [path, state] of Object.entries(states.fields)) {
        if (state.filter === undefined || state.visible === false)
            continue;
        const match = PATH.exec(path);
        if (match === null)
            continue;
        const located = match[2] === undefined ? template.sections.flatMap((section) => (isList(section) ? [] : section.fields.map((entry) => ({ section, entry })))).find((candidate) => candidate.entry.field.field_id === match[1]) : (() => {
            const section = template.sections.find((candidate) => candidate.section_id === match[1]);
            const entry = section?.fields.find((candidate) => candidate.field.field_id === match[3]);
            return section === undefined || entry === undefined ? undefined : { section, entry };
        })();
        const field = located?.entry.field;
        if (field === undefined || field.type !== 'reference' || typeof field.reference_type !== 'string')
            continue;
        // Réponses brutes (chaîne) ou relues par le serveur (identifiant d'objet).
        const value = answerAt(answers, path);
        const id = typeof value === 'string' ? value : value !== null && typeof value === 'object' && typeof value.toHexString === 'function' ? value.toHexString() : '';
        if (id === '')
            continue;
        const own = (located.entry.presentation?.selector?.filter ?? {});
        const filter = Object.fromEntries(Object.entries(state.filter).filter(([key]) => own[key] === undefined));
        if (Object.keys(filter).length === 0)
            continue;
        checks.push({ field: path, object_type: field.reference_type, id, filter });
    }
    return checks;
}
// ---- Compilation des réactions dérivées en conditions du gabarit.
function and(base, condition) {
    if (condition === true)
        return base ?? true;
    if (condition === false)
        return false;
    if (base === undefined || base === null || base === true)
        return condition;
    return { $and: [base, condition] };
}
function or(base, condition) {
    if (condition === false)
        return base;
    if (condition === true)
        return true;
    if (base === undefined || base === null || base === false)
        return condition;
    return { $or: [base, condition] };
}
const not = (condition) => (typeof condition === 'boolean' ? !condition : { $not: [condition] });
/** Remplace `$form.…` et `$inputs.…` par leur valeur : la condition d'une ligne ne lit plus que `$answers` de la ligne. */
function bindOuter(expression, scope) {
    if (typeof expression === 'string') {
        if (/^\$(form|inputs)(\.|$)/.test(expression)) {
            const value = attempt(expression, scope);
            return { $literal: value === undefined ? null : value };
        }
        return expression;
    }
    if (Array.isArray(expression))
        return expression.map((item) => bindOuter(item, scope));
    if (expression !== null && typeof expression === 'object') {
        const out = {};
        for (const [key, value] of Object.entries(expression))
            out[key] = key === '$literal' ? value : bindOuter(value, scope);
        return out;
    }
    return expression;
}
/**
 * Gabarit effectif : les réactions dérivées compilées dans les conditions que le rendu et la lecture du
 * serveur évaluent déjà. Une condition d'une ligne déclenchée par sa propre ligne reste une expression sur
 * `$answers` (la ligne) ; toute autre devient un littéral calculé sur les réponses données. Sans réaction
 * dérivée, le gabarit est rendu tel quel (même objet).
 */
export function effectiveTemplate(template, answers, inputs = {}, options = {}) {
    const derived = derivedActions(template);
    if (derived.length === 0)
        return template;
    const copy = clone(template);
    const entryOf = (located) => copy.sections.find((section) => section.section_id === located.section.section_id).fields.find((entry) => entry.field.field_id === located.entry.field.field_id);
    const restrict = options.restrictOptions !== false;
    for (const { action, target, group } of derived) {
        const kind = action['action'];
        if (kind === 'state' && target === undefined) {
            const pageId = action['target_page'];
            const page = copy.layout?.pages?.find((candidate) => candidate.page_id === pageId);
            if (page === undefined)
                continue;
            const active = holds(action['when'], plainScope(answers, inputs));
            const condition = action['effect'] === 'show' ? active : action['effect'] === 'hide' ? !active : true;
            const next = and(page.visible_when, condition);
            if (next !== undefined)
                page.visible_when = next;
            continue;
        }
        if (target === undefined)
            continue;
        const entry = entryOf(target);
        const rowBound = target.list && group === target.section.section_id;
        // Ligne déclenchée par sa propre ligne : expression sur la ligne, `$form` et `$inputs` déjà lus.
        const condition = rowBound ? (action['when'] === undefined ? true : bindOuter(action['when'], { form: answers, inputs })) : holds(action['when'], plainScope(answers, inputs));
        if (kind === 'state') {
            const effect = action['effect'];
            if (effect === 'show' || effect === 'hide') {
                const next = and(entry['visible_when'], effect === 'show' ? condition : not(condition));
                if (next === undefined)
                    delete entry['visible_when'];
                else
                    entry['visible_when'] = next;
            }
            else if (effect === 'require' || effect === 'readonly') {
                const key = effect === 'require' ? 'required_when' : 'readonly_when';
                const next = or(entry[key], condition);
                if (next === undefined)
                    delete entry[key];
                else
                    entry[key] = next;
            }
            continue;
        }
        // `reload` d'une ligne déclenchée par sa propre ligne : par ligne seulement ({@link reactionStates}).
        if (rowBound || condition !== true)
            continue;
        const scope = plainScope(answers, inputs);
        if (action['options'] !== undefined && restrict) {
            const allowed = allowedOptions(target.entry, attempt(action['options'], scope));
            const field = entry['field'];
            const validation = field['validation'];
            if (allowed !== undefined && validation !== undefined) {
                const current = validation['enum_values'] ?? [];
                validation['enum_values'] = current.filter((value) => allowed.includes(value));
            }
        }
        if (action['filter'] !== undefined) {
            const presentation = (entry['presentation'] ??= {});
            const selector = (presentation['selector'] ??= {});
            const filter = (selector['filter'] ??= {});
            for (const [key, value] of Object.entries(evaluatedFilter(action['filter'], scope)))
                if (filter[key] === undefined)
                    filter[key] = value;
        }
    }
    return copy;
}
function matches(reaction, event) {
    const trigger = reaction.trigger;
    if (trigger.on !== event.on)
        return false;
    if (event.on === 'change')
        return trigger.field?.section_id === event.field?.section_id && trigger.field?.field_id === event.field?.field_id;
    if (event.on === 'page_validate')
        return trigger.page_id === undefined || trigger.page_id === event.page_id;
    if (event.on === 'row_add')
        return trigger.section_id === event.section_id;
    return true;
}
function messageText(text, scope, template, answers) {
    const out = {};
    if (text === null || typeof text !== 'object')
        return out;
    const values = formAggregates(template, answers);
    for (const [language, expression] of Object.entries(text)) {
        const value = attempt(expression, { ...scope, aggregates: aggregateTextScope(values, language) });
        if (typeof value === 'string' && value !== '')
            out[language] = value;
    }
    return out;
}
function fire(template, run, event, depth, inputs) {
    for (const reaction of reactionsOf(template)) {
        if (!matches(reaction, event))
            continue;
        const group = triggerList(template, reaction);
        const row = group === null ? undefined : event.row;
        if (group !== null && row === undefined)
            continue;
        const scope = () => (group === null ? plainScope(run.answers, inputs) : rowScope(run.answers, group, row, inputs));
        const sourcePath = event.on === 'change' && event.field !== undefined ? (row === undefined ? plainPath(event.field.field_id) : rowPath(event.field.section_id, row, event.field.field_id)) : event.on === 'row_add' && row !== undefined ? `answers.${event.section_id}.${row}` : undefined;
        for (const action of reaction.actions ?? []) {
            const kind = action['action'];
            if (kind !== 'set' && kind !== 'message')
                continue;
            run.actions += 1;
            if (run.actions > REACTION_MAX_ACTIONS) {
                run.limit = true;
                return;
            }
            if (kind === 'message') {
                const values = formAggregates(template, run.answers);
                if (!holds(action['when'], { ...scope(), aggregates: aggregateScope(values) }))
                    continue;
                const message = {
                    reaction_id: reaction.reaction_id,
                    kind: action['kind'] ?? 'info',
                    text: messageText(action['text'], scope(), template, run.answers),
                    ...(reaction.label === undefined ? {} : { label: reaction.label }),
                    ...(action['confirm_label'] === undefined ? {} : { confirm_label: action['confirm_label'] }),
                    ...(action['cancel_label'] === undefined ? {} : { cancel_label: action['cancel_label'] }),
                    ...(sourcePath === undefined ? {} : { path: sourcePath }),
                };
                if (message.kind === 'confirm') {
                    if (run.confirm === undefined && event.on !== 'open')
                        run.confirm = message;
                }
                else
                    run.messages.push(message);
                continue;
            }
            // `set` : réponse suggérée, jamais une saisie (ADR-144 §3.2).
            const target = locate(template, action['target']);
            if (target === undefined || target.entry.compute !== undefined)
                continue;
            let path;
            if (target.list) {
                if (group !== target.section.section_id || row === undefined)
                    continue;
                path = rowPath(group, row, target.entry.field.field_id);
            }
            else
                path = plainPath(target.entry.field.field_id);
            const local = scope();
            if (!holds(action['when'], local))
                continue;
            const value = attempt(action['value'], local);
            if (value === undefined)
                continue;
            const current = answerAt(run.answers, path);
            const ours = run.origins[path]?.origin === 'suggested';
            if (!empty(current) && (event.on === 'open' || !ours))
                continue;
            if (same(current, value))
                continue;
            if (empty(value)) {
                if (!ours)
                    continue;
                writeAt(run.answers, path, undefined);
                delete run.origins[path];
            }
            else {
                writeAt(run.answers, path, value);
                run.origins[path] = { origin: 'suggested', source: `reaction:${reaction.reaction_id}` };
                if (!run.suggested.includes(path))
                    run.suggested.push(path);
            }
            // Cascade : la valeur a changé, son `change` se déclenche (quatre niveaux au plus ; au-delà, la borne est signalée
            // seulement si une réaction l'attendait).
            const cascade = { on: 'change', field: { section_id: target.section.section_id, field_id: target.entry.field.field_id }, ...(row === undefined || !target.list ? {} : { row }) };
            if (depth + 1 > REACTION_MAX_DEPTH) {
                if (reactionsOf(template).some((candidate) => matches(candidate, cascade)))
                    run.limit = true;
                continue;
            }
            fire(template, run, cascade, depth + 1, inputs);
            if (run.actions > REACTION_MAX_ACTIONS)
                return;
        }
    }
}
/** Vide les champs masqués par une réaction `reverse_and_clear` ; un vidage peut en masquer d'autres. */
function clearHidden(template, run, inputs) {
    const cleared = [];
    for (let pass = 0; pass <= REACTION_MAX_DEPTH; pass += 1) {
        const { clears } = reactionStates(template, run.answers, inputs);
        const pending = clears.filter((path) => !empty(answerAt(run.answers, path)));
        if (pending.length === 0)
            break;
        for (const path of pending) {
            writeAt(run.answers, path, undefined);
            delete run.origins[path];
            if (!cleared.includes(path))
                cleared.push(path);
        }
    }
    return cleared;
}
function start(state) {
    return { answers: clone(state.answers), origins: { ...state.origins }, messages: [], suggested: [], actions: 0, limit: false };
}
function finish(template, run, before, inputs) {
    const cleared = clearHidden(template, run, inputs);
    return {
        answers: run.answers,
        origins: run.origins,
        messages: run.messages,
        ...(run.confirm === undefined ? {} : { confirm: run.confirm, cancel: { answers: clone(before.answers), origins: { ...before.origins } } }),
        cleared,
        suggested: run.suggested,
        limit_reached: run.limit,
    };
}
/** Ouverture de la fiche (après la reprise d'un brouillon) : `open` ne remplit que des champs vides. */
export function openForm(template, state, context = {}) {
    const inputs = context.inputs ?? {};
    const run = start(state);
    fire(template, run, { on: 'open' }, 0, inputs);
    return finish(template, run, state, inputs);
}
/**
 * Ouverture d'une fiche **neuve** (ADR-146 addendum (21)) : les lignes qu'elle ouvre d'office (`series.min_count`)
 * sont des ajouts de ligne comme les autres — `open` joue sur la fiche sans ses lignes, puis chaque ligne initiale
 * déclenche `row_add`. Messages des deux temps, dans l'ordre ; aucune confirmation à l'ouverture. Un brouillon
 * repris passe par {@link openForm} : ses lignes ne sont pas des ajouts.
 */
export function openNewForm(template, answers, context = {}) {
    const lists = template.sections.filter((section) => section.series !== undefined && Array.isArray(answers[section.section_id]) && answers[section.section_id].length > 0);
    if (lists.length === 0)
        return openForm(template, { answers, origins: {} }, context);
    const bare = { ...answers };
    for (const section of lists)
        bare[section.section_id] = [];
    const opened = openForm(template, { answers: bare, origins: {} }, context);
    const next = { ...opened.answers };
    for (const section of lists)
        next[section.section_id] = clone(answers[section.section_id]);
    const added = reconcileAnswers(template, opened, next, context);
    return { answers: added.answers, origins: added.origins, messages: [...opened.messages, ...added.messages], cleared: [...opened.cleared, ...added.cleared.filter((path) => !opened.cleared.includes(path))], suggested: [...opened.suggested, ...added.suggested.filter((path) => !opened.suggested.includes(path))], limit_reached: opened.limit_reached || added.limit_reached };
}
/** « Suivant » ou « Valider » sur une page (`pageId` absent : la fiche sans pages). */
export function validatePage(template, state, pageId, context = {}) {
    const inputs = context.inputs ?? {};
    const run = start(state);
    fire(template, run, { on: 'page_validate', ...(pageId === undefined ? {} : { page_id: pageId }) }, 0, inputs);
    return finish(template, run, state, inputs);
}
function shiftOrigins(origins, sectionId, removed) {
    const out = {};
    const prefix = `answers.${sectionId}.`;
    for (const [path, origin] of Object.entries(origins)) {
        if (!path.startsWith(prefix)) {
            out[path] = origin;
            continue;
        }
        const [index, ...rest] = path.slice(prefix.length).split('.');
        const position = Number(index);
        if (position === removed)
            continue;
        out[position > removed ? `${prefix}${position - 1}.${rest.join('.')}` : path] = origin;
    }
    return out;
}
/**
 * Nouvelles réponses données par le rendu (une valeur validée, une ligne ajoutée ou retirée) : les
 * changements sont relevés par différence avec l'état d'avant, dans l'ordre du gabarit, puis leurs
 * réactions s'exécutent. Une réponse que l'opérateur modifie perd sa marque « suggérée ».
 */
export function reconcileAnswers(template, before, next, context = {}) {
    const inputs = context.inputs ?? {};
    let origins = { ...before.origins };
    const events = [];
    for (const section of template.sections) {
        if (!isList(section)) {
            for (const entry of section.fields) {
                const id = entry.field.field_id;
                if (same(before.answers[id], next[id]))
                    continue;
                delete origins[plainPath(id)];
                events.push({ on: 'change', field: { section_id: section.section_id, field_id: id } });
            }
            continue;
        }
        const id = section.section_id;
        const previous = rowsOf(before.answers, id);
        const rows = rowsOf(next, id);
        if (rows.length < previous.length) {
            // Une ligne retirée : les marques des lignes suivantes remontent d'un rang.
            let removed = rows.findIndex((row, index) => !same(row, previous[index]));
            if (removed < 0)
                removed = rows.length;
            for (let count = previous.length - rows.length; count > 0; count -= 1)
                origins = shiftOrigins(origins, id, removed);
            continue;
        }
        rows.forEach((row, index) => {
            if (index >= previous.length) {
                events.push({ on: 'row_add', section_id: id, row: index });
                return;
            }
            for (const entry of section.fields) {
                const fieldId = entry.field.field_id;
                if (same(previous[index]?.[fieldId], row?.[fieldId]))
                    continue;
                delete origins[rowPath(id, index, fieldId)];
                events.push({ on: 'change', field: { section_id: id, field_id: fieldId }, row: index });
            }
        });
    }
    const run = start({ answers: next, origins });
    for (const event of events) {
        fire(template, run, event, 0, inputs);
        if (run.limit && run.actions > REACTION_MAX_ACTIONS)
            break;
    }
    return finish(template, run, before, inputs);
}
const PATH = /^answers\.([a-z][a-z0-9_]*)(?:\.(\d+)\.([a-z][a-z0-9_]*))?$/;
/** Réponses hors des valeurs proposées par une réaction `reload` (`FORM_OPTION_NOT_ALLOWED`). */
export function optionFindings(template, answers, inputs = {}) {
    const findings = [];
    const states = reactionStates(template, answers, inputs);
    for (const [path, state] of Object.entries(states.fields)) {
        if (state.options === undefined || state.visible === false)
            continue;
        const value = answerAt(answers, path);
        if (empty(value))
            continue;
        if (typeof value !== 'string' || !state.options.includes(value)) {
            findings.push({ field: path, code: 'FORM_OPTION_NOT_ALLOWED', message: `« ${String(value)} » n’est pas proposé pour ce champ dans l’état de la fiche.` });
        }
    }
    return findings;
}
/**
 * Rejoue les suggestions déclarées par le client (`answer_origins`) sur les réponses finales : une marque
 * qui ne vient d'aucune réaction `set` déclarée est refusée (`FORM_ORIGIN_INVALID`) ; une suggestion que
 * le serveur ne retrouve plus garde la réponse validée et devient `suggested_stale` (jamais un refus).
 */
export function replayAnswerOrigins(template, answers, declared, inputs = {}) {
    const errors = [];
    const origins = {};
    if (declared === undefined || declared === null)
        return { errors, origins };
    if (typeof declared !== 'object' || Array.isArray(declared)) {
        errors.push({ field: 'answer_origins', code: 'FORM_ORIGIN_INVALID', message: 'Les origines des réponses sont une table par chemin.' });
        return { errors, origins };
    }
    const reactions = reactionsOf(template);
    for (const [path, raw] of Object.entries(declared)) {
        const origin = raw;
        const match = PATH.exec(path);
        const source = typeof origin?.source === 'string' ? origin.source : '';
        const reaction = reactions.find((candidate) => `reaction:${candidate.reaction_id}` === source);
        const action = reaction?.actions.find((candidate) => {
            if (candidate['action'] !== 'set' || match === null)
                return false;
            const target = candidate['target'];
            return match[2] === undefined ? target?.field_id === match[1] && locate(template, target)?.list === false : target?.section_id === match[1] && target?.field_id === match[3];
        });
        if (origin === null || typeof origin !== 'object' || origin.origin !== 'suggested' || match === null || reaction === undefined || action === undefined) {
            errors.push({ field: `answer_origins.${path}`, code: 'FORM_ORIGIN_INVALID', message: 'Cette réponse est marquée « proposée », mais aucune réaction du formulaire ne la propose.' });
            continue;
        }
        const value = answerAt(answers, path);
        if (empty(value))
            continue;
        const group = triggerList(template, reaction);
        const scope = match[2] === undefined || group === null ? plainScope(answers, inputs) : rowScope(answers, match[1], Number(match[2]), inputs);
        const expected = holds(action['when'], scope) ? attempt(action['value'], scope) : undefined;
        origins[path] = expected !== undefined && same(expected, value) ? { origin: 'suggested', source } : { origin: 'suggested_stale', source, suggested_value: expected === undefined ? null : expected };
    }
    return { errors, origins };
}
//# sourceMappingURL=form-reactions.js.map